import styled from 'styled-components';
import {ResponsiveContainer, AreaChart, Area, Tooltip} from 'recharts';
import React, {useState} from 'react';
import Pagination from './Pagination';
import Button from './Button';
import Menu from './Menu';
import Icon from './Icon';
import Number from './Number';
import WidgetHeader from './WidgetHeader';
import * as api from '../lib/api';
import {useCredentials} from '../lib/user';


function LineChart({data, valueKey, onClick, question, preview, options, id, unPin, dashboardWidgetSet, step, setStep, formulaDeletedQuestions, tooltip = () => null}) {
  const credentials = useCredentials();

  if(dashboardWidgetSet) {
    data.forEach(answer => {
      answer.value = parseInt(answer.value)
    })
    data.sort((a,b) => b.lastModified - a.lastModified);
  }

  const values = data.map(datum => datum[valueKey]).sort((a, b) => a - b);
  const sum = values.reduce((a, b) => a + b, 0);
  const avg = sum / values.length;
  const min = values[0];
  const max = values[values.length-1];

  let defaultStartStep = step ? step : 10;
  let defaultValueToDisplay = 10;
  if(dashboardWidgetSet) {
    if(!(question.step === 0)) {
      defaultStartStep = question.step;
      defaultValueToDisplay = question.step;
      if(question.step === -1) {
        defaultStartStep = values.length
        defaultValueToDisplay = "All"
      }
    }
  }

  let start, end;
  const children = [];
  var title = question.title ? question.title : question.text;
  var [pointsToDisplay, setPointsToDisplay] = useState(defaultStartStep);
  const [page, setPage] = useState(Math.ceil(values.length/pointsToDisplay));
  var [valueToDisplay, setValueToDisplay] = useState(defaultValueToDisplay );
  const [isChartMenuOpen, toggleChartMenuState] = useState(false);

  const toggleChartMenu = () => {
    isChartMenuOpen ? toggleChartMenuState(false) : toggleChartMenuState(true);
  }

  const reDraw = (e) => {
    if (e < 0) {
      setPointsToDisplay(values.length);
      setValueToDisplay("All");
    }
    else {
        setPage(Math.ceil(values.length/e));
        setPointsToDisplay(e);
        setValueToDisplay(e);
    }
    setStep(e);

    if(dashboardWidgetSet) {
      api.updatePinnedWidget(id, e, credentials);
    }
  }

  var optionSet=[
    {title: 'Show 10', value: 10, onClick: () => reDraw(10)},
    {title: 'Show 20', value: 20, onClick: () => reDraw(20)},
    {title: 'Show 50', value: 50, onClick: () => reDraw(50)},
    {title: 'Show 100', value: 100, onClick: () => reDraw(100)},
    {title: 'Show 200', value: 200, onClick: () => reDraw(200)},
    {title: 'Show 500', value: 500, onClick: () => reDraw(500)},
    {title: 'Show All', value: "all", onClick: () => reDraw(-1)},
    {title: 'Show 1000', value: 1000, onClick: () => reDraw(1000)}
  ]

  var pageCalc = page;

  if(page === 1) {
    start = 0;
    end = pointsToDisplay;
  } else {
      pageCalc--;
      start = pageCalc*pointsToDisplay;
      end = pageCalc*pointsToDisplay + Math.min(parseInt(pointsToDisplay), values.length);
  }

  if(pointsToDisplay === values.length) {
    start = 0;
    end = values.length;
  }

  var dataToDisplay = data.slice().reverse().slice(start, end);

  let filteredOptions = options;
  if(question.pinned) {
    children.push(
      <Button key={`${id}_key`} id={`${id}_pin`} tertiary onClick={() => unPin(question)}>
        <Icon name="go-pin" />
      </Button>
    )
    filteredOptions = options.filter(option => option.title !== "Pin");
  }


  if(data.length > 10) {
    children.push(
      <React.Fragment>
        <StyledButton id={`${question.id}_menu`} tertiary onClick={() => toggleChartMenu()}>Show {`${valueToDisplay}`}   <Icon name="chevron-down" /></StyledButton>
        <StyledMenu open={isChartMenuOpen} onClose={() => toggleChartMenu(false)} relativeTo={`${question.id}_menu`} position="bottom-end">
          {optionSet.map((option, i) => {
            const {title, ...buttonProps} = option;
              if(option.value < values.length || (values.length < 1000 && option.value === "all") ) {
                return <StyledMenu.Option key={i} style={{color: '#107aff'}} {...buttonProps}>{title}</StyledMenu.Option>;
              }
              return null;
          })}
        </StyledMenu>
      </React.Fragment>)
  }

  return (
    <React.Fragment>
      <WidgetHeader
        id={id}
        preview={preview}
        title={title}
        question={question}
        options={filteredOptions}
        formulaDeletedQuestions={formulaDeletedQuestions}
        children={children}
      />
      <Container>
        <Statistics>
          <Stat>
            <Metric>Average</Metric>
            <Value><Number>{avg}</Number></Value>
          </Stat>
          <Stat>
            <Metric>Minimum</Metric>
            <Value><Number>{min}</Number></Value>
          </Stat>
          <Stat>
            <Metric>Maximum</Metric>
            <Value><Number>{max}</Number></Value>
          </Stat>
          <Stat>
            <Metric>Total</Metric>
            <Value><Number>{sum}</Number></Value>
          </Stat>
        </Statistics>
        <Chart>
          <ResponsiveContainer width={'100%'} key={pointsToDisplay}>
            <AreaChart data={dataToDisplay}>
              <defs>
                <linearGradient id="areaColor" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0049ee" stopOpacity={0.1} />
                  <stop offset="95%" stopColor="#0049ee" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Tooltip
                content={<LineChartTooltip renderer={tooltip} />}
                animationDuration={0}
              />
              <Area
                type="monotone"
                dataKey={valueKey}
                stroke="#0049ee"
                fillOpacity={1}
                fill="url(#areaColor)"
                dot
                activeDot={{onClick}}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Chart>
        <WidgetFooter>
            <PaginationContainer>
              <Pagination
                mini={true}
                disabled={false}
                page={parseInt(page)}
                totalItems={values.length}
                itemsPerPage={pointsToDisplay}
                onClick={e => setPage(e)}
              />
            </PaginationContainer>
          </WidgetFooter>
      </Container>
    </React.Fragment>
  );
}

function LineChartTooltip({active, payload: [point], label, renderer}) {
  if (!active || !point) {
    return null;
  }

  return renderer(point.payload, label);
}

const Container = styled.div`
  height: 100%;
  overflow-y: hidden;
`;

const StyledButton = styled(Button)`
  color: #107aff;
  :hover {
    background: transparent !important;
  }
  :focus {
    outline: none;
  }
`;

const StyledMenu = styled(Menu)`
  color: #107aff;
  .Option{
    color: #107aff;
  }
`;

const Statistics = styled.div`
  display: flex;
  padding: 0 1.875rem;
  height: 4rem;
`;

const Stat = styled.div`
  flex: 1;
`;

const Metric = styled.h5`
  margin: 0;
  font-size: 0.9rem;
  font-weight: 400;
  color: #747474;
`;

const Value = styled.p`
  margin: 0;
  margin-top: 0.5rem;
  font-size: 1.25rem;
`;

const Chart = styled.div`
  height: 30rem;
  padding-bottom: .25rem;
  overflow: hidden;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  overflow: auto;
`;

const WidgetFooter = styled.div`
  margin-bottom: .25rem;
  overflow: auto;
`;

export default LineChart;
